/**
 * GrowthMeasurementList
 *
 * Displays a chronological list of baby growth measurements.
 */

import { useState } from 'react';
import { Scale, Ruler, Circle, Trash2, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { BabyGrowthEntry } from '@/services/babyGrowthService';
import {
  Gender,
  calculatePercentile,
  getPercentileLabel,
  formatAge,
} from '../data/whoGrowthStandards';

interface GrowthMeasurementListProps {
  entries: BabyGrowthEntry[];
  gender: Gender;
  birthDate: string;
  onEdit?: (entry: BabyGrowthEntry) => void;
  onDelete?: (id: string) => void;
}

// Format weight from grams
// Shows g or kg with up to 3 decimal places, removes trailing zeros
function formatWeight(grams: number): string {
  if (grams >= 1000) {
    const kg = grams / 1000;
    // Use up to 3 decimal places, then remove trailing zeros
    const formatted = kg.toFixed(3).replace(/\.?0+$/, '');
    return `${formatted} kg`;
  }
  return `${grams} g`;
}

// Format date for display
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// Single measurement item
interface MeasurementItemProps {
  value: string;
  percentile?: number;
  icon: React.ReactNode;
}

function MeasurementItem({ value, percentile, icon }: MeasurementItemProps) {
  const percentileInfo = percentile !== undefined ? getPercentileLabel(percentile) : null;

  return (
    <div className="flex items-center gap-2">
      <div className="text-white/40">{icon}</div>
      <div className="flex-1">
        <span className="text-white text-sm font-medium">{value}</span>
        {percentileInfo && (
          <span className={`ml-2 text-xs ${percentileInfo.color}`}>
            P{percentile}
          </span>
        )}
      </div>
    </div>
  );
}

export function GrowthMeasurementList({
  entries,
  gender,
  birthDate,
  onEdit,
  onDelete,
}: GrowthMeasurementListProps) {
  const { t } = useTranslation('babyGrowth');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Calculate age in days for a measurement
  const getAgeInDays = (measuredAt: string): number => {
    if (!birthDate) return 0;
    const birth = new Date(birthDate);
    const measured = new Date(measuredAt);
    return Math.floor((measured.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Handle delete confirmation
  const handleDeleteClick = (id: string) => {
    if (confirmDeleteId === id) {
      onDelete?.(id);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(id);
      // Auto-reset after 3 seconds
      setTimeout(() => setConfirmDeleteId(null), 3000);
    }
  };

  if (entries.length === 0) {
    return (
      <div className="rounded-[15px] p-6 text-center">
        <p className="text-white/60 text-sm">
          {t('measurementList.empty')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {entries.map((entry, index) => {
        const isExpanded = expandedId === entry.id;
        const ageInDays = getAgeInDays(entry.measured_at);
        const isConfirmingDelete = confirmDeleteId === entry.id;

        // Calculate percentiles
        const weightPercentile = entry.weight_grams
          ? calculatePercentile(entry.weight_grams, ageInDays, 'weight', gender)
          : undefined;
        const heightPercentile = entry.height_cm
          ? calculatePercentile(Number(entry.height_cm), ageInDays, 'height', gender)
          : undefined;
        const headPercentile = entry.head_circumference_cm
          ? calculatePercentile(Number(entry.head_circumference_cm), ageInDays, 'head', gender)
          : undefined;

        return (
          <div key={entry.id}>
            <div className={`rounded-[15px] overflow-hidden transition-colors ${isExpanded ? 'bg-white/10' : ''}`}>
              {/* Header - always visible */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                className="w-full py-3 px-1 flex items-start gap-3 text-left hover:bg-white/5 transition-colors"
              >
                {/* Icon */}
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Scale className="w-5 h-5 text-white/25" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium text-sm">
                      {formatDate(entry.measured_at)}
                    </span>
                    <span className="text-white/50 text-sm">
                      {formatAge(ageInDays)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-white/70 text-sm mt-0.5">
                    {entry.weight_grams && (
                      <span>{formatWeight(entry.weight_grams)}</span>
                    )}
                    {entry.height_cm && (
                      <span>{Number(entry.height_cm).toFixed(1)} cm</span>
                    )}
                    {entry.head_circumference_cm && (
                      <span>KU {Number(entry.head_circumference_cm).toFixed(1)} cm</span>
                    )}
                  </div>
                  {entry.notes && (
                    <p className="text-pp-orange text-xs mt-1">{entry.notes}</p>
                  )}
                </div>

                {/* Expand icon */}
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-white/40 flex-shrink-0 mt-3" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0 mt-3" />
                )}
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-0 border-t border-white/10">
                  <div className="pt-3 space-y-2">
                    {/* Weight */}
                    {entry.weight_grams && (
                      <MeasurementItem
                        value={formatWeight(entry.weight_grams)}
                        percentile={weightPercentile}
                        icon={<Scale className="w-4 h-4" />}
                      />
                    )}

                    {/* Height */}
                    {entry.height_cm && (
                      <MeasurementItem
                        value={`${Number(entry.height_cm).toFixed(1)} cm`}
                        percentile={heightPercentile}
                        icon={<Ruler className="w-4 h-4" />}
                      />
                    )}

                    {/* Head Circumference */}
                    {entry.head_circumference_cm && (
                      <MeasurementItem
                        value={`${Number(entry.head_circumference_cm).toFixed(1)} cm`}
                        percentile={headPercentile}
                        icon={<Circle className="w-4 h-4" />}
                      />
                    )}

                    {/* Measured by */}
                    {entry.measured_by_name && (
                      <p className="text-white/40 text-xs mt-2">
                        {t('measurementList.measuredBy', { name: entry.measured_by_name })}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/10">
                    {onEdit && (
                      <button
                        onClick={() => onEdit(entry)}
                        className="flex-1 flex items-center justify-center gap-2 py-2 rounded-[10px] bg-white/10 text-white/70 hover:text-white hover:bg-white/20 transition-colors text-sm"
                      >
                        <Edit2 className="w-4 h-4" />
                        {t('measurementList.edit')}
                      </button>
                    )}
                    {onDelete && entry.notes !== 'Geburt' && (
                      <button
                        onClick={() => handleDeleteClick(entry.id)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-[10px] transition-colors text-sm ${
                          isConfirmingDelete
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-white/10 text-white/70 hover:text-red-400 hover:bg-red-500/10'
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                        {isConfirmingDelete ? t('measurementList.confirmDelete') : t('measurementList.delete')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            {index < entries.length - 1 && !isExpanded && (
              <div className="flex justify-center">
                <div className="w-full h-px bg-white/25" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
